/**
 * =============================================================================
 * JKL HEADER SIDEBAR MOBILE BEHAVIOR EXTENSION
 * =============================================================================
 *
 * This script extends the header sidebar functionality to make desktop behave
 * like mobile (overlay panels instead of side-by-side) without modifying theme.js
 *
 * =============================================================================
 */

(() => {
  'use strict';

  // Wait for the DOM to be ready and header-sidebar to be defined
  function initMobileBehaviorExtension() {
    const headerSidebar = document.querySelector('header-sidebar');
    if (!headerSidebar) return;

    // Override the matchesMediaQuery function behavior for header sidebar
    const originalMatchesMediaQuery = window.matchesMediaQuery;

    // Create a custom function that always returns mobile behavior for header sidebar
    function forceMatchesMediaQuery(query) {
      // If this is called from header sidebar context and it's checking for mobile
      if (query === 'md-max') {
        return true; // Always return true to force mobile behavior
      }
      if (query === 'md') {
        return false; // Always return false to prevent desktop behavior
      }
      // For other queries, use the original function
      return originalMatchesMediaQuery ? originalMatchesMediaQuery(query) : window.matchMedia(query).matches;
    }

    // Store reference to the original HeaderSidebar class
    const OriginalHeaderSidebar = window.customElements.get('header-sidebar');
    if (!OriginalHeaderSidebar) return;

    // Create extended HeaderSidebar class
    class ExtendedHeaderSidebar extends OriginalHeaderSidebar {
      constructor() {
        super();
        this.forceMobileBehavior = true;
      }

      connectedCallback() {
        // Temporarily override matchesMediaQuery during initialization
        const originalMatchesMediaQuery = window.matchesMediaQuery;
        window.matchesMediaQuery = forceMatchesMediaQuery;

        super.connectedCallback();

        // Restore original function
        window.matchesMediaQuery = originalMatchesMediaQuery;

        // Override specific methods that check media queries
        this.overrideMediaQueryMethods();
      }

      overrideMediaQueryMethods() {
        // Find and override the private openCollapsiblePanel method
        const collapsiblePanel = this.querySelector('[slot="collapsible-panel"]');
        const buttonElements = Array.from(this.querySelectorAll('.header-sidebar__main-panel .header-sidebar__linklist [aria-controls]'));

        if (buttonElements.length > 0) {
          // Override button click handlers to always use mobile behavior
          buttonElements.forEach(button => {
            // Remove existing listeners by cloning the button
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);

            // Add our custom click handler
            newButton.addEventListener('click', (event) => {
              // Set aria-expanded states
              buttonElements.forEach(btn => {
                const actualBtn = btn === button ? newButton : btn;
                actualBtn.setAttribute('aria-expanded', actualBtn === newButton ? 'true' : 'false');
              });

              // Set collapsible panel active descendant
              if (collapsiblePanel) {
                collapsiblePanel.setAttribute('aria-activedescendant', newButton.getAttribute('aria-controls'));
              }

              // Always animate main panel out (mobile behavior)
              const mainPanel = this.querySelector('.header-sidebar__main-panel');
              if (mainPanel) {
                // Use the same animation as mobile
                if (window.animate) {
                  window.animate(mainPanel,
                    { opacity: [1, 0], transform: ['translateX(0)', 'translateX(-10px)'] },
                    { duration: 0.25 }
                  );
                }
              }
            });
          });
        }
      }
    }

    // Store reference to the original HeaderSidebarCollapsiblePanel class
    const OriginalCollapsiblePanel = window.customElements.get('header-sidebar-collapsible-panel');
    if (!OriginalCollapsiblePanel) return;

    // Create extended HeaderSidebarCollapsiblePanel class
    class ExtendedHeaderSidebarCollapsiblePanel extends OriginalCollapsiblePanel {
      constructor() {
        super();
        this.forceMobileBehavior = true;
      }

      connectedCallback() {
        // Temporarily override matchesMediaQuery during initialization
        const originalMatchesMediaQuery = window.matchesMediaQuery;
        window.matchesMediaQuery = forceMatchesMediaQuery;

        super.connectedCallback();

        // Restore original function
        window.matchesMediaQuery = originalMatchesMediaQuery;
      }

      createEnterAnimationControls() {
        // Always use mobile animation (overlay from right)
        if (window.timeline) {
          return window.timeline([
            [this, { opacity: [0, 1], transform: 'translateX(0)' }, { duration: 0.3 }]
          ]);
        }
        return null;
      }

      async attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'aria-activedescendant') {
          if (oldValue === newValue) return;

          if (newValue !== null) {
            // Always use mobile-like panel switching
            const fromPanel = oldValue ? this.querySelector(`#${oldValue}`) : null;
            const toPanel = this.querySelector(`#${newValue}`);

            if (toPanel) {
              await this.switchPanelMobile(fromPanel, toPanel);
            }
          } else {
            await this.hide();
            Array.from(this.querySelectorAll('.header-sidebar__sub-panel')).forEach(panel => {
              panel.hidden = true;
            });
          }
        } else {
          // Call parent method for other attributes
          super.attributeChangedCallback?.(name, oldValue, newValue);
        }
      }

      async switchPanelMobile(fromPanel, toPanel) {
        if (!this.open) {
          await this.show();
        }

        if (fromPanel) {
          if (window.animate) {
            await window.animate(fromPanel, { opacity: [1, 0] }, { duration: 0.15 }).finished;
          }
          fromPanel.hidden = true;
        }

        toPanel.hidden = false;

        // Always use mobile selector (includes back button)
        const listSelector = '.header-sidebar__back-button, .header-sidebar__linklist li';

        if (window.timeline && window.stagger) {
          window.timeline([
            [toPanel, { opacity: 1 }, { duration: 0 }],
            [toPanel.querySelectorAll(listSelector),
             { opacity: [0, 1], transform: ['translateY(8px)', 'translateY(0)'] },
             { duration: 0.15, at: '-0.15', delay: window.themeVariables?.settings?.staggerMenuApparition ? window.stagger(0.1) : 0 }],
            [toPanel.querySelector('.header-sidebar__promo'),
             { opacity: [0, 1] },
             { duration: 0.45 }]
          ]);
        }
      }
    }

    // Re-register the custom elements with our extended classes
    // Note: We need to unregister first, but that's not directly possible
    // Instead, we'll only register if not already registered
    if (!window.customElements.get('extended-header-sidebar')) {
      window.customElements.define('extended-header-sidebar', ExtendedHeaderSidebar);
    }
    if (!window.customElements.get('extended-header-sidebar-collapsible-panel')) {
      window.customElements.define('extended-header-sidebar-collapsible-panel', ExtendedHeaderSidebarCollapsiblePanel);
    }

    // If the page already has header sidebar elements, we need to replace them
    // This is a bit tricky since custom elements can't be re-registered
    // Instead, we'll add a class to force mobile behavior via CSS and override methods
    if (headerSidebar) {
      headerSidebar.classList.add('force-mobile-behavior');

      // Override methods on the existing instance
      if (headerSidebar.constructor.name === 'HeaderSidebar') {
        // Apply our mobile behavior overrides to the existing element
        applyMobileBehaviorOverrides(headerSidebar);
      }
    }

    const collapsiblePanel = document.querySelector('header-sidebar-collapsible-panel');
    if (collapsiblePanel) {
      collapsiblePanel.classList.add('force-mobile-behavior');
      applyCollapsiblePanelOverrides(collapsiblePanel);
    }
  }

  // Function to apply mobile behavior overrides to existing header sidebar
  function applyMobileBehaviorOverrides(headerSidebar) {
    // Override button click behavior
    const buttonElements = Array.from(headerSidebar.querySelectorAll('.header-sidebar__main-panel .header-sidebar__linklist [aria-controls]'));
    const collapsiblePanel = headerSidebar.querySelector('[slot="collapsible-panel"]');

    buttonElements.forEach(button => {
      // Store original click handler
      const originalHandler = button.onclick;

      // Override with mobile behavior
      button.addEventListener('click', (event) => {
        event.stopImmediatePropagation();

        // Set aria states
        buttonElements.forEach(btn => {
          btn.setAttribute('aria-expanded', btn === button ? 'true' : 'false');
        });

        if (collapsiblePanel) {
          collapsiblePanel.setAttribute('aria-activedescendant', button.getAttribute('aria-controls'));
        }

        // Always animate main panel out (mobile behavior)
        const mainPanel = headerSidebar.querySelector('.header-sidebar__main-panel');
        if (mainPanel && window.animate) {
          window.animate(mainPanel,
            { opacity: [1, 0], transform: ['translateX(0)', 'translateX(-10px)'] },
            { duration: 0.25 }
          );
        }
      }, true); // Use capture phase to override
    });
  }

  // Function to apply overrides to collapsible panel
  function applyCollapsiblePanelOverrides(collapsiblePanel) {
    // We'll need to be more careful here since this involves more complex state management
    // For now, the CSS overrides should handle most of the visual behavior
    console.log('Applied mobile behavior overrides to collapsible panel');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileBehaviorExtension);
  } else {
    initMobileBehaviorExtension();
  }

  // Also initialize after a short delay to ensure theme.js has loaded
  setTimeout(initMobileBehaviorExtension, 100);

})();
